import { OfflineEntity } from '../types/database.types'
import { offlineDatabase } from './database'

export abstract class BaseOfflineStore<T extends OfflineEntity> {
    protected storeName: string

    constructor(storeName: string) {
        this.storeName = storeName
    }

    protected getStore(mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction(this.storeName, mode)
        return transaction.objectStore(this.storeName)
    }

    async add(item: T): Promise<T> {
        return new Promise((resolve, reject) => {
            const store = this.getStore('readwrite')
            const request = store.add(item)

            request.onsuccess = () => {
                resolve(item)
            }

            request.onerror = () => {
                reject(new Error(`Failed to add item to ${this.storeName}`))
            }
        })
    }

    async update(item: T): Promise<T> {
        return new Promise((resolve, reject) => {
            const store = this.getStore('readwrite')
            const request = store.put(item)

            request.onsuccess = () => {
                resolve(item)
            }

            request.onerror = () => {
                console.error(`Failed to update item in ${this.storeName}:`, {
                    item,
                    error: request.error,
                    errorMessage: request.error?.message
                })
                reject(new Error(`Failed to update item in ${this.storeName}`))
            }
        })
    }

    async delete(id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const store = this.getStore('readwrite')
            const request = store.delete(id)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error(`Failed to delete item from ${this.storeName}`))
            }
        })
    }

    async get(id: string): Promise<T | undefined> {
        return new Promise((resolve, reject) => {
            const store = this.getStore()
            const request = store.get(id)

            request.onsuccess = () => {
                resolve(request.result)
            }

            request.onerror = () => {
                reject(new Error(`Failed to get item from ${this.storeName}`))
            }
        })
    }

    async getAll(): Promise<T[]> {
        return new Promise((resolve, reject) => {
            const store = this.getStore()
            const request = store.getAll()

            request.onsuccess = () => {
                resolve(request.result)
            }

            request.onerror = () => {
                reject(new Error(`Failed to get all items from ${this.storeName}`))
            }
        })
    }

    async getByIndex(indexName: string, value: any): Promise<T[]> {
        return new Promise((resolve, reject) => {
            const store = this.getStore()
            const index = store.index(indexName)
            const request = index.getAll(value)

            request.onsuccess = () => {
                resolve(request.result)
            }

            request.onerror = () => {
                reject(new Error(`Failed to get items by index from ${this.storeName}`))
            }
        })
    }

    async getPendingSync(): Promise<T[]> {
        return this.getByIndex('_syncStatus', 'pending')
    }

    async getFailedSync(): Promise<T[]> {
        return this.getByIndex('_syncStatus', 'failed')
    }
} 